const ConditionalFactory = require("./conditionalFactory");

class PropertyFilter {
  // property = A the value is A
  // property = A,B,C
  // property = 12~30 in between 12 and 30
  // property = A||B||C is A or B or C

  constructor() {
    this.invalids = [];
  }

  /**
   * @param  {string} filter - The propertyfilter string to evaluate
   * @param  {Object} obj - The object to evaluate against
   */
  isValid(filter, obj) {
    if (!filter) {
      return true;
    }

    if (typeof filter !== "string") {
      throw new Error("Propertyfilter already parsed");
    }

    let valid = false;
    const rule = this.parse(filter);
    valid = this.validate(rule, obj);
    return valid;
  }

  /**
   * @param  {string} rule - Propertyfilter string from promaster
   * @param  {object} options - Options for the parsing
   */
  parse(rule, options = {}) {
    if (rule === "" || !rule) {
      return null;
    }

    return ConditionalFactory.create(rule, options);
  }

  /**
   * @param  {Conditional} rule - A parsed conditional statement
   * @param  {Object} obj - Any object to compare against
   */
  validate(rule, obj) {
    // No rule, so always valid
    if (rule === null || rule === undefined) {
      return true;
    }

    if (typeof rule !== "object") {
      throw new Error(
        `Propertyfilter is not an object. It has not been parsed, ${rule}`
      );
    }

    this.invalids = [];
    let valid = false;

    if (rule.property === "statement") {
      valid = this.validStatement(rule, obj);
    } else {
      switch (rule.operator) {
        case "!=":
          valid = this.validNotEquals(rule, obj);
          break;
        case "=":
          valid = this.validEquals(rule, obj);
          break;
        case "!||":
          valid = this.validNotOr(rule, obj);
          break;
        case "||":
          valid = this.validOr(rule, obj);
          break;
        case "!~":
          valid = this.validNotBetween(rule, obj);
          break;
        case "~":
          valid = this.validBetween(rule, obj);
          break;
        default:
          break;
      }
      if (!valid) {
        this.invalids.push(rule);
      }
    }
    return valid;
  }

  validNotEquals(rule, obj) {
    const objValue = obj[rule.property];
    const compare = rule.value;

    if (Array.isArray(objValue)) {
      const trueIndex = objValue.findIndex((x) => x === compare);
      return trueIndex === -1;
    }
    return compare !== objValue;
  }

  checkIfEquals(compare, objValue) {
    if (Array.isArray(objValue)) {
      const trueIndex = objValue.findIndex((x) => x === compare);
      return trueIndex >= 0;
    }
    return compare === objValue;
  }

  validEquals(rule, obj) {
    const objValue = obj[rule.property];
    const compare = rule.value;

    return this.checkIfEquals(compare, objValue);
  }

  validNotOr(rule, obj) {
    let objValue = obj[rule.property];
    const compare = rule.value;

    if (objValue && !isNaN(objValue)) {
      objValue = parseInt(objValue, 10);
    }

    const trueIndex = compare.findIndex((x) => this.checkIfEquals(x, objValue));

    return trueIndex === -1;
  }

  validOr(rule, obj) {
    let objValue = obj[rule.property];
    const compare = rule.value;

    if (objValue && !isNaN(objValue)) {
      objValue = parseInt(objValue, 10);
    }

    const trueIndex = compare.findIndex((x) => this.checkIfEquals(x, objValue));

    return trueIndex >= 0;
  }

  validBetween(rule, obj) {
    const objValue = obj[rule.property];
    const compare = rule.value;

    if (Array.isArray(objValue)) {
      const trueIndex = objValue.findIndex(
        (x) => x >= compare[0] && x <= compare[1]
      );
      return trueIndex >= 0;
    }

    return objValue >= compare[0] && objValue <= compare[1];
  }

  validNotBetween(rule, obj) {
    const objValue = obj[rule.property];
    const compare = rule.value;

    if (Array.isArray(objValue)) {
      const trueIndex = objValue.findIndex(
        (x) => x >= compare[0] && x <= compare[1]
      );
      return trueIndex === -1;
    }

    return objValue < compare[0] || objValue > compare[1];
  }

  validStatement(rule, obj) {
    let valid = false;
    let found = false;
    switch (rule.operator) {
      case "&&":
        for (let i = 0; i < rule.value.length; i++) {
          const r = rule.value[i];
          if (!r) {
            console.log("Empty rule", rule);
            continue;
          }
          if (!this.validate(r, obj)) {
            found = true;
            break;
          }
        }
        if (!found) {
          valid = true;
        }
        break;
      case "||":
        for (let i = 0; i < rule.value.length; i++) {
          const r = rule.value[i];
          if (this.validate(r, obj)) {
            found = true;
            break;
          }
        }
        if (found || rule.value.length === 0) {
          valid = true;
        }
        break;
      default:
        throw new Error(
          `Operator not recognized: ${rule.operator} in rule: property=${rule.property}, unitSpecifier=${rule.unitSpecifier}, value=${rule.value}`
        );
    }
    return valid;
  }

  // We only validate the properties that exist in the object
  isValidOptimistic(filter, obj) {
    let valid = false;
    if (!filter) {
      return true;
    }
    // Handle exclusion rules, i.e. 1=0, 0=1
    const objToValidate = { 1: 1, 0: 0, ...obj };
    let rule =
      typeof filter === "string"
        ? this.parse(filter, { cache: false })
        : JSON.parse(JSON.stringify(filter));
    rule = this.cleanOptimistic(rule, objToValidate);
    valid = this.validate(rule, objToValidate);
    return valid;
  }

  cleanOptimistic(rule, obj) {
    let value = null;
    if (rule.property === "statement") {
      rule.value = rule.value.map((r) => this.cleanOptimistic(r, obj));
      rule.value = rule.value.filter((r) => r !== null && r !== undefined);
      value = rule;
    } else {
      if (obj.hasOwnProperty(rule.property)) {
        value = rule;
      }
    }
    return value;
  }
}

module.exports = new PropertyFilter();
